import React, { useMemo, useState } from 'react';
import { EyeOffIcon, LockIcon, SendIcon, StickyNoteIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { Field, Input, Select, Textarea } from '../../components/ui/Field';
import { EmptyState } from '../../components/ui/EmptyState';
import { useTickets } from '../../contexts/TicketContext';
import type { Ticket, TicketPriority, TicketStatus } from '../../types/tickets';
const QUEUES = ['Needs a reply', 'Escalated', 'Resolved', 'Everything'] as const;
const STATUSES: TicketStatus[] = ['Open', 'Awaiting your reply', 'With support', 'Escalated', 'Resolved', 'Closed'];
const PRIORITIES: TicketPriority[] = ['Low', 'Normal', 'High', 'Urgent'];
const AGENTS = ['Support — Ngozi', 'Trust & safety — Adaeze', 'Finance operations — Tunde', 'Housing operations — Kelechi'];
function when(at: string): string {
  return new Date(at).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
}
export function SupportDesk() {
  const {
    tickets,
    agentReply,
    setStatus,
    setPriority,
    assign,
    escalate,
    resolve
  } = useTickets();
  const [queue, setQueue] = useState<(typeof QUEUES)[number]>('Needs a reply');
  const [selectedId, setSelectedId] = useState<string | null>(tickets[0]?.id ?? null);
  const filtered = useMemo(() => {
    if (queue === 'Escalated') return tickets.filter(ticket => ticket.status === 'Escalated');
    if (queue === 'Resolved') return tickets.filter(ticket => ticket.status === 'Resolved' || ticket.status === 'Closed');
    if (queue === 'Everything') return tickets;
    return tickets.filter(ticket => ticket.status === 'Open' || ticket.status === 'With support' || ticket.status === 'Escalated');
  }, [queue, tickets]);
  const selected = tickets.find(ticket => ticket.id === selectedId) ?? filtered[0] ?? null;
  return <div className="space-y-6" data-id="element-7087" data-mp="3ko8ho2w3ylwh">
      <PageHeader eyebrow="Operations" title="Support desk" description="Every ticket raised across Campuzon. An agent sees the ticket and the record it names — nothing else about the person." actions={<Badge tone="warn" icon={<LockIcon className="h-4 w-4" data-id="element-7090" data-mp="c98228e215pn" />} data-id="element-7089" data-mp="c8or5bmtnhl4">
            Sensitive access is recorded
          </Badge>} data-id="element-7088" data-mp="3fqgg0h5nvlf3" />

      <Tabs tabs={QUEUES} value={queue} onChange={setQueue} label="Ticket queues" data-id="element-7091" data-mp="78kddtdat0yl" />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)]" data-id="element-7092" data-mp="29qwrs2pstu7">
        <Panel title={`${filtered.length} ticket${filtered.length === 1 ? '' : 's'}`} bodyClassName="p-0 sm:p-0" data-id="element-7093" data-mp="3uekzvogga2zg">
          {filtered.length === 0 ? <div className="px-5 py-8" data-id="element-7094" data-mp="2iz4q6q38ebad">
              <EmptyState icon={<SendIcon className="h-5 w-5" data-id="element-7096" data-mp="yif2qqfsea84" />} title="This queue is clear" description="Nothing here needs an agent right now." data-id="element-7095" data-mp="2j2afa5bv1d63" />
            </div> : <ul className="divide-y divide-line" data-id="element-7097" data-mp="17ibnigz22oxw">
              {filtered.map(ticket => <li key={ticket.id} data-id="element-7098" data-mp="1ca3eutq1cyp3">
                  <button type="button" onClick={() => setSelectedId(ticket.id)} aria-current={selected?.id === ticket.id || undefined} className={`w-full px-5 py-4 text-left transition-colors duration-150 ease-out hover:bg-canvas ${selected?.id === ticket.id ? 'bg-canvas' : ''}`} data-id="element-7099" data-mp="1cb4pix353j6a">
                    <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1" data-id="element-7100" data-mp="1v61uwc5tv6fr">
                      <p className="text-body font-semibold text-ink" data-id="element-7101" data-mp="3s32c2ezadgu9">{ticket.subject}</p>
                      {ticket.priority === 'Urgent' && <Badge tone="danger" data-id="element-7102" data-mp="31pcg4yl7hp2a">Urgent</Badge>}
                    </div>
                    <p className="cz-num mt-0.5 text-body-sm text-ink-muted" data-id="element-7103" data-mp="31rfbly54oaee">
                      {ticket.reference} · @{ticket.raisedBy}
                    </p>
                    <p className="mt-1 text-caption text-ink-subtle" data-id="element-7104" data-mp="3bna21fflf9rq">
                      {ticket.category} · updated {when(ticket.updatedAt)}
                    </p>
                  </button>
                </li>)}
            </ul>}
        </Panel>

        {selected ? <TicketDetail key={selected.id} ticket={selected} onReply={(body, internal) => agentReply(selected.id, body, {
        internal
      })} onStatus={status => setStatus(selected.id, status)} onPriority={priority => setPriority(selected.id, priority)} onAssign={agent => assign(selected.id, agent)} onEscalate={reason => escalate(selected.id, reason)} onResolve={resolution => resolve(selected.id, resolution)} data-id="element-7105" data-mp="2e1l29qk0p3yw" /> : <Panel title="No ticket selected" data-id="element-7106" data-mp="fi7vvhvsjxkp">
            <p className="text-body-sm text-ink-muted" data-id="element-7107" data-mp="fj9hggafo2go">Choose a ticket from the queue.</p>
          </Panel>}
      </div>
    </div>;
}
function TicketDetail({
  ticket,
  onReply,
  onStatus,
  onPriority,
  onAssign,
  onEscalate,
  onResolve
}: {
  ticket: Ticket;
  onReply: (body: string, internal: boolean) => void;
  onStatus: (status: TicketStatus) => void;
  onPriority: (priority: TicketPriority) => void;
  onAssign: (agent: string) => void;
  onEscalate: (reason: string) => void;
  onResolve: (resolution: string) => void;
}) {
  const [draft, setDraft] = useState('');
  const [internal, setInternal] = useState(false);
  const [resolution, setResolution] = useState('');
  const [escalation, setEscalation] = useState('');
  return <div className="space-y-5" data-id="element-7108" data-mp="359wcj0cl6888">
      <Panel title={ticket.subject} description={`${ticket.reference} · raised by @${ticket.raisedBy} · ${when(ticket.openedAt)}`} data-id="element-7109" data-mp="399n2x2y7amc8">
        <div className="grid gap-5 sm:grid-cols-3" data-id="element-7110" data-mp="1fsh99m2mp01f">
          <Field label="Status" htmlFor="ticket-status" data-id="element-7111" data-mp="1asct5x5mu7bq">
            <Select id="ticket-status" value={ticket.status} onChange={event => onStatus(event.target.value as TicketStatus)} data-id="element-7112" data-mp="1atnza3mjvr60">
              {STATUSES.map(status => <option key={status} value={status} data-id="element-7113" data-mp="x0g7aauk4tns">
                  {status}
                </option>)}
            </Select>
          </Field>
          <Field label="Priority" htmlFor="ticket-priority" data-id="element-7114" data-mp="s2wocxmwzspi">
            <Select id="ticket-priority" value={ticket.priority} onChange={event => onPriority(event.target.value as TicketPriority)} data-id="element-7115" data-mp="n2ihvk0yoqtl">
              {PRIORITIES.map(priority => <option key={priority} value={priority} data-id="element-7116" data-mp="2hde2xltc1yd9">
                  {priority}
                </option>)}
            </Select>
          </Field>
          <Field label="Assigned to" htmlFor="ticket-agent" data-id="element-7117" data-mp="2kwwa8f6v3uih">
            <Select id="ticket-agent" value={ticket.assignedTo ?? ''} onChange={event => onAssign(event.target.value)} data-id="element-7118" data-mp="2oyrcpn0j4dxu">
              <option value="" data-id="element-7119" data-mp="2yp2tia5bahm5">Unassigned</option>
              {AGENTS.map(agent => <option key={agent} value={agent} data-id="element-7120" data-mp="2yqx4avmvctci">
                  {agent}
                </option>)}
            </Select>
          </Field>
        </div>

        <dl className="mt-5 grid gap-4 border-t border-line pt-4 sm:grid-cols-2" data-id="element-7121" data-mp="260x77iym0kk7">
          <div data-id="element-7122" data-mp="2aygiqb3xaj8g">
            <dt className="text-body-sm text-ink-muted" data-id="element-7123" data-mp="2axx3j25nq8y4">Record named on the ticket</dt>
            <dd className="cz-num mt-0.5 text-body font-medium text-ink" data-id="element-7124" data-mp="2fze2gs4kzlc2">
              {ticket.context.kind === 'none' ? 'None' : `${ticket.context.label} (${ticket.context.reference})`}
            </dd>
          </div>
          <div data-id="element-7125" data-mp="gcecx18tt18s">
            <dt className="text-body-sm text-ink-muted" data-id="element-7126" data-mp="gbwhu7xwgt2g">Reporter identity</dt>
            <dd className="mt-0.5 text-body font-medium text-ink" data-id="element-7127" data-mp="cat4bs3br94e">
              {ticket.confidential ? 'Withheld from the reported party' : 'Visible to the desk only'}
            </dd>
          </div>
        </dl>

        <div className="mt-4 space-y-2 rounded-card border border-line bg-canvas p-4" data-id="element-7128" data-mp="lbr8qbhodini">
          <p className="flex items-start gap-2 text-body-sm text-ink" data-id="element-7129" data-mp="100dmw9vkg4d5">
            <EyeOffIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-7130" data-mp="zykil18fdw8b" />
            Not available from this desk: notes, diary, wellbeing activity, private messages, full
            financial identifiers and balances.
          </p>
          {ticket.context.masked && <p className="flex items-start gap-2 text-body-sm text-ink" data-id="element-7131" data-mp="142s6iw72785h">
              <LockIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-7132" data-mp="3athtlsoydyxs" />
              The transaction reference is masked here. Opening the full entry requires finance
              operations and is written to the audit log against the agent's name.
            </p>}
        </div>
      </Panel>

      <Panel title="Conversation" bodyClassName="p-0 sm:p-0" data-id="element-7133" data-mp="1q9w6o0a0j14z">
        <ul className="divide-y divide-line" data-id="element-7134" data-mp="1cjsuk24uknt9">
          {ticket.messages.map(message => <li key={message.id} className={`px-5 py-4 sm:px-6 ${message.internal ? 'bg-warn-50' : ''}`} data-id="element-7135" data-mp="1gk31pi4dlk9p">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1" data-id="element-7136" data-mp="12jifebz956bg">
                <p className="text-body-sm font-semibold text-ink" data-id="element-7137" data-mp="17kpm07pnrzgj">{message.authorName}</p>
                {message.internal && <Badge tone="warn" icon={<StickyNoteIcon className="h-4 w-4" data-id="element-7139" data-mp="xlgwbr1tt6zz" />} data-id="element-7138" data-mp="tm80mgn86z1s">
                    Internal note — not shown to the reporter
                  </Badge>}
                {message.author === 'system' && <Badge plain data-id="element-7140" data-mp="2rzhue9pjxpz0">Automatic</Badge>}
                <p className="cz-num text-caption text-ink-subtle" data-id="element-7141" data-mp="2rygffu5f0jc0">{when(message.at)}</p>
              </div>
              <p className="mt-1.5 max-w-[76ch] text-body text-ink" data-id="element-7142" data-mp="uk51eh00o33x">{message.body}</p>
            </li>)}
        </ul>

        <div className="border-t border-line px-5 py-4 sm:px-6" data-id="element-7143" data-mp="ze98r1d72d5j">
          <Field label={internal ? 'Internal note' : 'Reply to the reporter'} htmlFor="agent-reply" data-id="element-7144" data-mp="kp4n1a76xqso">
            <Textarea id="agent-reply" rows={3} value={draft} onChange={event => setDraft(event.target.value)} placeholder={internal ? 'Context for other agents. The reporter never sees this.' : 'Say what you found and what happens next.'} data-id="element-7145" data-mp="pn645pl23g1i" />
          </Field>
          <div className="mt-3 flex flex-wrap items-center gap-3" data-id="element-7146" data-mp="18bb74utw2wz9">
            <Button variant="primary" icon={<SendIcon className="h-4 w-4" data-id="element-7148" data-mp="3t3dak3uuvrdz" />} disabled={!draft.trim()} onClick={() => {
            onReply(draft.trim(), internal);
            setDraft('');
          }} data-id="element-7147" data-mp="3p0ox1iv75wao">
              {internal ? 'Save internal note' : 'Send reply'}
            </Button>
            <Button variant="ghost" onClick={() => setInternal(value => !value)} data-id="element-7149" data-mp="3vhqggy7k9m9f">
              {internal ? 'Reply to the reporter instead' : 'Make this an internal note'}
            </Button>
          </div>
        </div>
      </Panel>

      <div className="grid gap-5 lg:grid-cols-2" data-id="element-7150" data-mp="32u4etvhw8rp7">
        <Panel title="Escalate" description="Sends it to a senior queue and records the reason." data-id="element-7151" data-mp="2xu9q9yllce7b">
          <Field label="Why" htmlFor="escalate-reason" data-id="element-7152" data-mp="3cj5utlofv936">
            <Input id="escalate-reason" value={escalation} onChange={event => setEscalation(event.target.value)} placeholder="Needs finance operations to inspect the ledger" data-id="element-7153" data-mp="3ckpb3ca3hk4y" />
          </Field>
          <Button variant="secondary" className="mt-3" disabled={!escalation.trim()} onClick={() => {
          onEscalate(escalation.trim());
          setEscalation('');
        }} data-id="element-7154" data-mp="2f4a4ploylh8n">
            Escalate ticket
          </Button>
        </Panel>

        <Panel title="Resolve" description="The reason is shown to the reporter. A ticket cannot be closed silently." data-id="element-7155" data-mp="3dl92mphucd4i">
          <Field label="How it was resolved" htmlFor="resolve-note" data-id="element-7156" data-mp="3il54ai32fsgw">
            <Textarea id="resolve-note" rows={3} value={resolution} onChange={event => setResolution(event.target.value)} data-id="element-7157" data-mp="2lumhbgwh4np1" />
          </Field>
          <Button variant="primary" className="mt-3" disabled={!resolution.trim()} onClick={() => {
          onResolve(resolution.trim());
          setResolution('');
        }} data-id="element-7158" data-mp="2usew8jya9tbw">
            Mark resolved
          </Button>
        </Panel>
      </div>
    </div>;
}